import React, { useState,useMemo } from 'react';
import { Search, Users, Building, Calendar, CheckCircle, Mail, Phone, MapPin, Star, ArrowRight, Briefcase, List } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { useServices, useJobs, useTestimonials ,useSiteSettings,applyJob,forEmployeers} from '../hooks/useAPI';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Form, FormField, FormItem, FormLabel, FormControl } from '../components/ui/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useForm } from 'react-hook-form';
import Logo from '../assets/images/logo.png';
import { toast } from 'sonner';

const resumeFields = [
  { name: 'jobs_id', label: 'Job ID', type: 'text', required: true },
  { name: 'name', label: 'Full Name', type: 'text', required: true },
  { name: 'email', label: 'Email', type: 'email', required: true },
  { name: 'phone_number', label: 'Phone Number', type: 'text', required: true },
  { name: 'years_of_experience', label: 'Years of Experience', type: 'number', required: true },
  { name: 'current_role', label: 'Current Role', type: 'text', required: true },
  { name: 'preferred_location', label: 'Preferred Location', type: 'text', required: true },
  { name: 'expected_salary_range', label: 'Expected Salary Range', type: 'text', required: true },
  { name: 'availability', label: 'Availability', type: 'select', required: true },
  { name: 'key_skills_expertise', label: 'Key Skills & Expertise', type: 'text', required: true },
  { name: 'linkedIn_profile', label: 'LinkedIn Profile', type: 'text', required: true },
  { name: 'resume', label: 'Resume', type: 'file', required: true },
  { name: 'career_goals_additional_information', label: 'Career Goals / Additional Information', type: 'textarea', required: true },
];

const availabilityOptions = [
  { value: 'immediately', label: 'Immediately' },
  { value: '2-weeks-notice', label: '2 weeks notice' },
  { value: '1-month-notice', label: '1 month notice' },
  { value: 'flexible', label: 'Flexible' }
];

const employerFields = [
  { name: 'name', label: 'Full Name', type: 'text', placeholder: 'Your full name', required: true },
  { name: 'email', label: 'Email Address', type: 'email', placeholder: 'your.email@company.com', required: true },
  { name: 'company', label: 'Company Name', type: 'text', placeholder: 'Your company name', required: true },
  { name: 'phone', label: 'Phone Number', type: 'text', placeholder: '+1 (555) 123-4567', required: true },
  { name: 'requirements', label: 'Hiring Requirements', type: 'textarea', placeholder: 'Describe the roles you need to fill, timeline, and specific requirements...', required: true },
];

const UpdatedHomePage = () => {
const [searchInput, setSearchInput] = useState('');
const [jobSearchQuery, setJobSearchQuery] = useState('');
  const [selectedIndustry, setSelectedIndustry] = useState('all');
  const [jobsPage, setJobsPage] = useState(1);
  const [resumeDialogOpen, setResumeDialogOpen] = useState(false);
  const [employerDialogOpen, setEmployerDialogOpen] = useState(false);

  const form = useForm({
    defaultValues: {
      jobs_id: '',
      name: '',
      email: '',
      phone_number: '',
      years_of_experience: '',
      current_role: '',
      preferred_location: '',
      expected_salary_range: '',
      availability: '',
      key_skills_expertise: '',
      linkedIn_profile: '',
      resume: null,
      career_goals_additional_information: '',
    },
    mode: 'onChange'
  });

  const employerForm = useForm({
    defaultValues: {
      name: '',
      email: '',
      company: '',
      phone: '',
      requirements: '',
    },
    mode: 'onChange'
  });

  // API hooks
  const { data: services, loading: servicesLoading } = useServices();
  // const { data: jobsData, loading: jobsLoading } = useJobs({
  //   search: jobSearchQuery || undefined,
  //   industry: selectedIndustry !== 'all' ? selectedIndustry : undefined,
  //   page: jobsPage,
  //   limit: 6
  // });
  const jobsParams = useMemo(() => ({
    search: jobSearchQuery || undefined,
    industry: selectedIndustry !== 'all' ? selectedIndustry : undefined,
    page: jobsPage,
    limit: 6
  }), [jobSearchQuery, selectedIndustry, jobsPage]);

  const { data: jobsData, loading: jobsLoading } = useJobs(jobsParams);

  const { data: testimonials, loading: testimonialsLoading } = useTestimonials();
  const { data: sitesettings, loading: sitesettingsLoading } = useSiteSettings();

  // const industries = [
  //   { id: 'it', name: 'Information Technology', icon: Cpu },
  //   { id: 'engineering', name: 'Engineering', icon: Wrench },
  //   { id: 'pharmaceutical', name: 'Pharmaceutical', icon: Heart },
  //   { id: 'medical-device', name: 'Medical Device', icon: Microscope },
  //   { id: 'biotechnology', name: 'Biotechnology', icon: Briefcase }
  // ];

  // Handle job search
  const handleSearch = () => {
     setJobSearchQuery(searchInput);
     setJobsPage(1);

    // refetchJobs();
  };

  // Handle industry filter
  const handleIndustryFilter = (industryId) => {
    // alert(industryId);
    setSelectedIndustry(industryId);
    setJobsPage(1);
  };

  // Handle form submissions
  const handleCareerConsultation = async () => {
    try {
      // redirec to the url external link
      window.open(sitesettings?.schedule_call_link, '_blank');
      // This would typically open a modal or form
      toast.success("Career consultation form would open here");
    } catch (error) {
      toast.error("Failed to open consultation form");
    }
  };
  
  // open resume dialog and optionally prefill job id
  const handleResumeSubmission = (jobId) => {
    if (jobId) {
      try {
        form.setValue('jobs_id', jobId);
      } catch (err) {
        // setValue may fail if form not initialized yet — ignore
      }
    }
    setResumeDialogOpen(true);
  };

  // const handleResumeSubmission = () => {
  //   console.log('Button clicked, opening dialog...');
  //   setResumeDialogOpen(true);
  //   console.log('Dialog state set to true');
  // };

  const handleEmployerSubmission = () => {
    setEmployerDialogOpen(true);
  };

  const handleResumeFormSubmit = (data) => {
    // Convert file to just name for logging
    const logData = { ...data, resume: data.resume && data.resume[0] ? data.resume[0].name : null };
    // console.log('Resume Submission:', JSON.stringify(logData, null, 2));
    // submit the form
    var res = applyJob(JSON.stringify(logData, null, 2));
    console.log("Resume submission response:", res);
    if (res) {
      toast.success('Your request sent successfully. Will get back soon.');
    }
    setResumeDialogOpen(false);
    form.reset();
  };

  const handleEmployerFormSubmit = (data) => {
    console.log('Employer Requirements Submission:', JSON.stringify(data, null, 2));
     var res = forEmployeers(JSON.stringify(data, null, 2));
     if(res){
        toast.success('Request submitted successfully');
     }
    setEmployerDialogOpen(false);
    employerForm.reset();
  };

  //redirect to job details page
  const redirectToJobDetails = (jobId) => {
    // alert("clikked" + jobId);
    window.location.href = `/jobs/${jobId}`;
  };

 
  return (
    <div className="homepage">
      {/* Header */}
      <header className="header">
        <div className="container">
          <div className="nav-wrapper">
            <div className="logo">
             <a href='/'>
               <img src={Logo} alt="Renew Workforce" className="logo-image" style={{ height: '82px' }} />
             </a>
            </div>
            <nav className="nav">
              <a href="#services" className="nav-link">Services</a>
              <a href="#jobs" className="nav-link">Jobs</a>
              <a href="#about" className="nav-link">About</a>
              <a href="#contact" className="nav-link">Contact</a>
            </nav>
            <div className="nav-actions">
              <Button variant="outline" className="btn-secondary" onClick={handleEmployerSubmission}>
                For Employers
              </Button>
              <a href="#jobs">
                <Button className="btn-primary">
                  Career Opportunities
                </Button>
              </a>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero gradient-hero">
        <div className="gradient-overlay">
          <div className="container">
            <div className="hero-content">
              <div className="hero-text">
                <h1 className="display-hero animate-slide-up">
                  Rebuild Your U.S. Career with Confidence
                </h1>
                <p className="body-large hero-description animate-slide-up delay-200">
                  At Renew Workforce, we specialize in helping skilled professionals who have previously 
                  worked in the United States reclaim their career paths. Lost your work authorization? 
                  Don't worry—we provide legal guidance, reinstatement options, and strategic employer 
                  connections to help you get back on track. Your career deserves a second chance.
                </p>
                <div className="hero-actions animate-slide-up delay-300">
                  <Button className="btn-primary hero-btn" size="lg"  onClick={handleResumeSubmission}>
                    <Briefcase className="btn-icon" />
                    Start Career Revival
                  </Button>
                  <Button className="btn-secondary hero-btn" size="lg">
                    <Users className="btn-icon" />
                    Get Legal Guidance
                  </Button>
                </div>
                <div className="hero-stats animate-slide-up delay-400">
                  <div className="stat">
                    <span className="stat-number">500+</span>
                    <span className="stat-label">Careers Rebuilt</span>
                  </div>
                  <div className="stat">
                    <span className="stat-number">200+</span>
                    <span className="stat-label">Partner Employers</span>
                  </div>
                  <div className="stat">
                    <span className="stat-number">95%</span>
                    <span className="stat-label">Authorization Success Rate</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Career Rebuild Section */}
      <section className="career-rebuild-section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-large">Renew Workforce. Renew Opportunities.</h2>
            <p className="body-large section-description">
              Renew Workforce is an employee-centric staffing company dedicated to professionals 
              looking to re-enter the U.S. workforce. We understand that losing work authorization 
              can be discouraging, but it doesn't have to be the end of your career.
            </p>
          </div>
          <div className="rebuild-features">
            <div className="rebuild-feature">
              <div className="feature-icon-wrapper">
                <CheckCircle size={32} className="feature-icon-primary" />
              </div>
              <div className="feature-content">
                <h3 className="heading-medium">Legal Assistance for Work Authorization Reinstatement</h3>
                <p className="body-standard">Connect with immigration law experts who can evaluate your case and outline practical options.</p>
              </div>
            </div>
            <div className="rebuild-feature">
              <div className="feature-icon-wrapper">
                <Users size={32} className="feature-icon-primary" />
              </div>
              <div className="feature-content">
                <h3 className="heading-medium">Personalized Guidance to Navigate Visa Complexities</h3>
                <p className="body-standard">Expert support to help you understand and navigate complex immigration requirements.</p>
              </div>
            </div>
            <div className="rebuild-feature">
              <div className="feature-icon-wrapper">
                <Building size={32} className="feature-icon-primary" />
              </div>
              <div className="feature-content">
                <h3 className="heading-medium">Connections with Supportive Employers</h3>
                <p className="body-standard">Partner with employers willing to hire and support returning talent with previous U.S. experience.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Enhanced Services Section */}
      <section id="services" className="services-section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-large">Our Comprehensive Services</h2>
            <p className="body-large section-description">
              Complete support system designed to help you rebuild and advance your U.S. career
            </p>
          </div>
          <div className="services-grid">
            <Card className="service-card animate-slide-up delay-100">
              <CardHeader>
                <div className="service-icon">
                  <CheckCircle size={32} />
                </div>
                <CardTitle className="heading-medium">Work Authorization Reinstatement</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="body-standard">
                  Lost your U.S. work authorization? We connect you with immigration law experts who can evaluate your case and outline practical options.
                </CardDescription>
                <ul className="service-features">
                  <li className="feature-item">
                    <CheckCircle size={16} className="feature-icon" />
                    <span className="body-small">Immigration law consultations</span>
                  </li>
                  <li className="feature-item">
                    <CheckCircle size={16} className="feature-icon" />
                    <span className="body-small">Visa reinstatement options</span>
                  </li>
                  <li className="feature-item">
                    <CheckCircle size={16} className="feature-icon" />
                    <span className="body-small">Alternative employment visas</span>
                  </li>
                  <li className="feature-item">
                    <CheckCircle size={16} className="feature-icon" />
                    <span className="body-small">Legal documentation support</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="service-card animate-slide-up delay-200">
              <CardHeader>
                <div className="service-icon">
                  <Briefcase size={32} />
                </div>
                <CardTitle className="heading-medium">Career Revival & Job Placement</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="body-standard">
                  We match your skills and experience with employers who understand your situation and are ready to give you a second chance.
                </CardDescription>
                <ul className="service-features">
                  <li className="feature-item">
                    <CheckCircle size={16} className="feature-icon" />
                    <span className="body-small">Targeted employer matching</span>
                  </li>
                  <li className="feature-item">
                    <CheckCircle size={16} className="feature-icon" />
                    <span className="body-small">Resume optimization</span>
                  </li>
                  <li className="feature-item">
                    <CheckCircle size={16} className="feature-icon" />
                    <span className="body-small">Interview coaching</span>
                  </li>
                  <li className="feature-item">
                    <CheckCircle size={16} className="feature-icon" />
                    <span className="body-small">Salary negotiation support</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="service-card animate-slide-up delay-300">
              <CardHeader>
                <div className="service-icon">
                  <Users size={32} />
                </div>
                <CardTitle className="heading-medium">Personalized Employee Support</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="body-standard">
                  From legal consultations to career coaching, we support you every step of the way for a smooth transition back into the workforce.
                </CardDescription>
                <ul className="service-features">
                  <li className="feature-item">
                    <CheckCircle size={16} className="feature-icon" />
                    <span className="body-small">One-on-one career consultations</span>
                  </li>
                  <li className="feature-item">
                    <CheckCircle size={16} className="feature-icon" />
                    <span className="body-small">Legal guidance coordination</span>
                  </li>
                  <li className="feature-item">
                    <CheckCircle size={16} className="feature-icon" />
                    <span className="body-small">Professional development coaching</span>
                  </li>
                  <li className="feature-item">
                    <CheckCircle size={16} className="feature-icon" />
                    <span className="body-small">Ongoing career support</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* Industry Specializations */}
          <div className="industry-specializations">
            <h3 className="heading-large industry-title">Industry Specializations</h3>
            <p className="body-large industry-description">
              We focus on high-demand sectors where your expertise is most valued
            </p>
            <div className="industries-grid">
              {servicesLoading ? (
                <div className="loading-message">Loading industries...</div>
              ) : Array.isArray(services) ? (
                services.map((service, index) => {
                  return (
                    <div key={service.id} className={`industry-item animate-slide-up delay-${(index + 1) * 100}`}>
                      <img src={service.image} style={{height: '28px' }} alt={service.name} className="industry-icon" />
                      <span className="body-standard industry-name">{service.name}</span>
                    </div> 
                  );
                })
              ) : (
                <div className="no-services-message">No industries available.</div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Job Search Section */}
      <section id="jobs" className="jobs-section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-large">Discover Your Next Career Move</h2>
            <p className="body-large section-description">
              Premium opportunities with top USA companies actively seeking Indian talent
            </p>
          </div>
          
          <div className="job-search-wrapper">
            <div className="search-controls">
              <div className="form-container form-with-button">
                <Input
                  type="text"
                  placeholder="Search by job title or company..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="form-input"
                />
                <Button className="form-submit-embedded" onClick={handleSearch}>
                  <Search size={16} />
                  Search
                </Button> 
              </div>
              <div className="industry-filters">
                <Button
                  variant={selectedIndustry === 'all' ? 'default' : 'outline'}
                  onClick={() => handleIndustryFilter('all')}
                  className={selectedIndustry === 'all' ? "filter-btn animate-slide-up delay-100 btn btn-primary" : 'filter-btn animate-slide-up delay-100 btn '} 
                >
                  All Industries
                </Button>
                {Array.isArray(services) ? (
                  services.map((industry, index) => {
                    return (
                      <Button
                      key={industry.id}
                      variant={selectedIndustry === industry.id ? 'default' : 'outline'}
                      onClick={() => handleIndustryFilter(industry.id)}
                      className={selectedIndustry === industry.id ? ' filter-btn animate-slide-up delay-50 btn  btn-primary ' : 'filter-btn animate-slide-up delay-50 btn  outline'} 
                    >
                      {industry.name} 
                    </Button>
                    );
                  })
                ) : (
                  <div className="no-services-message">No industries available.</div>
                )}

              </div>
            </div>

            <div className="jobs-grid">
              {jobsLoading ? (
                <div className="loading-message">Loading jobs...</div>
              ) : jobsData?.jobs?.length > 0 ? (
                jobsData.jobs.map((job, index) => (
                    <div className='col-4  testimonial-content ' key={job.id}  style={{ border: '1px solid #e0e0e0', borderRadius: '8px', padding: '16px', marginBottom: '16px',boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)' }} >
                      <div className="job-header">
                        <div>
                          <CardTitle className="heading-medium job-title">{job.title}-{job.job_id}</CardTitle>
                          <p className="body-standard job-company">{job.company}</p>
                        </div>
                      </div>
                      <div className="job-details">
                        <div className="job-info">
                          <List size={16} className="info-icon" />
                          <span className="body-small">{job.job_id}</span>
                        </div>
                        <div className="job-info">
                          <MapPin size={16} className="info-icon" />
                          <span className="body-small">{job.location}</span>
                        </div>
                        <div className="job-info">
                          <Briefcase size={16} className="info-icon" />
                          <span className="body-small">{job.industry_details.name}</span>
                        </div>
                        <div className="job-info">
                          <span className="body-small salary">{job.salary}</span>
                        </div>
                      </div>
                       <blockquote
                        className="body-standard body-small job-description"
                        dangerouslySetInnerHTML={{ __html: job.description }}/>
                      
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '12px' }}>
                          <Button
                            className="btn-secondary job-apply-btn"
                            style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}
                            onClick={() => redirectToJobDetails(job.job_id)}
                          >
                            View Details 
                          </Button>

                          <Button
                             className="btn-primary contact-btn" onClick={()=>handleResumeSubmission(job.job_id)}
                          >
                            Apply Now
                            <ArrowRight size={16} />
                          </Button>
                      </div>
                    
                    </div>
                ))
              ) : (
                <div className="no-jobs-message">
                  <p>No jobs found matching your criteria.</p>
                </div>
              )}
            </div>
            
            <div className="jobs-footer">
              {/* <Button className="btn-secondary" size="lg">
                View All Jobs  Test Code
              </Button> */}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials-section gradient-waitlist">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-large">Success Stories</h2>
            <p className="body-large section-description">
              Hear from our satisfied clients and successful placements
            </p>
          </div>
          <div className="testimonials-grid">
            {testimonialsLoading ? (
              <div className="loading-message">Loading testimonials...</div>
            ) : testimonials?.length > 0 ? (
              testimonials.map((testimonial, index) => (
                <Card key={testimonial.id} className={`testimonial-card animate-slide-up delay-${(index + 1) * 200}`}>
                  <CardContent className="testimonial-content">
                    <div className="testimonial-rating">
                      {[...Array(parseInt(testimonial.rating))].map((_, i) => (
                        <Star key={i} size={16} className="star-filled" />
                      ))}
                    </div>
                      <blockquote
                        className="body-standard testimonial-quote"
                        dangerouslySetInnerHTML={{ __html: testimonial.content }}
                      />
                    <div className="testimonial-author">
                      <div className="author-info">
                        <h4 className="body-standard author-name">{testimonial.name}</h4>
                        <p className="body-small author-title">{testimonial.title}</p>
                        <p className="body-small author-company">{testimonial.company}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <div className="no-testimonials-message">
                <p>No testimonials available at the moment.</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="about-section">
        <div className="container">
          <div className="about-content">
            <div className="about-text">
              <h2 className="heading-large">Why Choose Renew Workforce?</h2>
              <p className="body-large about-description">
                Your career comeback is our top priority. We provide the expertise, connections, 
                and support you need to successfully re-enter the U.S. workforce with confidence.
              </p>
              <div className="about-highlights">
                <div className="highlight">
                  <Building size={24} className="highlight-icon" />
                  <div>
                    <h3 className="heading-medium">Expert Legal Network</h3>
                    <p className="body-standard">Access guidance from trusted immigration and employment law specialists who understand your unique situation</p>
                  </div>
                </div>
                <div className="highlight">
                  <Users size={24} className="highlight-icon" />
                  <div>
                    <h3 className="heading-medium">Targeted Employer Connections</h3>
                    <p className="body-standard">Work with employers who value your previous U.S. experience and are committed to supporting your return</p>
                  </div>
                </div>
                <div className="highlight">
                  <CheckCircle size={24} className="highlight-icon" />
                  <div>
                    <h3 className="heading-medium">Employee-Centric Approach</h3>
                    <p className="body-standard">Your career rebuild is our mission- all personal and legal matters handled with complete discretion and professionalism</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="contact-section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-large">Ready to Start Your USA Career Journey?</h2>
            <p className="body-large section-description">
              Take the first step towards your dream career. We're here to make it happen.
            </p>
          </div>
          <div className="contact-content">
            <div className="contact-options">
              <Card className="contact-option primary-option">
                <CardHeader>
                  <Briefcase size={32} className="contact-icon" />
                  <CardTitle className="heading-medium">Start Your Career Journey</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="body-standard">Upload your resume and let us connect you with your ideal employer</p>
                  <Button className="btn-primary contact-btn" onClick={handleResumeSubmission}>
                    Submit Resume
                    <ArrowRight size={16} />
                  </Button>
                </CardContent>
              </Card>
              
              <Card className="contact-option">
                <CardHeader>
                  <Calendar size={32} className="contact-icon" />
                  <CardTitle className="heading-medium">Career Consultation</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="body-standard">Book a free consultation to discuss your career goals</p>
                  
                  <Button className="btn-secondary contact-btn" onClick={handleCareerConsultation} >
                    Schedule Call
                    <Calendar size={16} />
                  </Button>
                </CardContent>
              </Card>
            </div>
            
            <div className="employer-cta">
              <p className="body-standard employer-text">
                Are you an employer looking for exceptional Indian talent? 
                <a href="#employer-contact"  onClick={handleEmployerSubmission}  className="employer-link">Partner with us</a>
              </p>
            </div>
            
            <div className="contact-info" style={{ marginTop: '24px' }}>
              <div className="contact-detail">
                <Mail size={20} className="contact-detail-icon" />
                <span className="body-standard">{sitesettings?.contact_email}</span>
              </div>  
              <div className="contact-detail">
                <Phone size={20} className="contact-detail-icon" />
                <span className="body-standard">{sitesettings?.contact_number}</span>
              </div>
              <div className="contact-detail">
                <MapPin size={20} className="contact-detail-icon" />
                <span className="body-standard">{sitesettings?.address}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-brand">
              <h3 className="heading-medium">Renew Workforce</h3>
              <p className="body-standard footer-description">
                Connecting exceptional talent with leading opportunities across specialized industries.
              </p>
            </div>
            <div className="footer-links">
              <div className="footer-column">
                <h4 className="body-standard footer-title">Services</h4>
                  <ul className="footer-list">
                  <li><a href="/services/it" className="footer-link">IT Staffing</a></li>
                  <li><a href="/services/engineering" className="footer-link">Engineering</a></li>
                  <li><a href="/services/pharmaceutical" className="footer-link">Pharmaceutical</a></li>
                  <li><a href="/services/medical-device" className="footer-link">Medical Device</a></li>
              </ul>
              </div>
              <div className="footer-column">
                <h4 className="body-standard footer-title">Company</h4>
                <ul className="footer-list">
                   <li><a href="#about" className="footer-link">About Us</a></li>
                   <li><a href="#jobs" className="footer-link">Careers</a></li>
                   <li><a href="#contact" className="footer-link">Contact</a></li>
                   <li><a href="#privacy-policy" className="footer-link">Privacy Policy</a></li>
               </ul>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            {sitesettingsLoading? (<>
               <p className="body-small">&copy; {sitesettings?.copyrights}. All rights reserved.</p>
            </>) :(<>
             <p className="body-small">&copy; {sitesettings?.copyrights}. All rights reserved.</p>
            </>)}
           
          </div>
        </div>
      </footer> 

      {/* Resume Submission Dialog */}
      <Dialog open={resumeDialogOpen} onOpenChange={setResumeDialogOpen}>
        <DialogContent style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: 'white',
          border: '1px solid #ccc',
          borderRadius: '8px',
          padding: '24px',
          maxWidth: '800px',
          width: '90%',
          maxHeight: '90vh',
          overflowY: 'auto',
          zIndex: 10000,
          boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
        }}>
          <DialogHeader>
            <DialogTitle>Submit Your Resume</DialogTitle>
            <DialogDescription>
              Fill out the form below and our team will get back to you soon.
            </DialogDescription>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleResumeFormSubmit)} style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '16px',
              marginTop: '10px'
            }}>
              {resumeFields.map((field) => (
                <FormField
                  key={field.name}
                  name={field.name}
                  control={form.control}
                  rules={{
                    required: field.required ? `${field.label} is required` : false,
                    ...(field.type === 'email' && {
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Invalid email address'
                      }
                    }),
                    ...(field.name === 'phone_number' && {
                      pattern: {
                        value: /^[\\+]?[1-9][\d]{0,15}$/,
                        message: 'Invalid phone number'
                      }
                    }),
                    ...(field.name === 'years_of_experience' && {
                      min: {
                        value: 0,
                        message: 'Years of experience must be 0 or greater'
                      },
                      max: {
                        value: 50,
                        message: 'Years of experience cannot exceed 50'
                      }
                    }),
                    ...(field.name === 'linkedIn_profile' && {
                      pattern: {
                        value: "",
                        message: 'Please enter a valid LinkedIn profile URL'
                      }
                    }),
                    ...(field.name === 'resume' && {
                      validate: (value) => {
                        if (!value || value.length === 0) {
                          return 'Resume file is required';
                        }
                        const file = value[0];
                        const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
                        if (!allowedTypes.includes(file.type)) {
                          return 'Please upload a PDF, DOC, or DOCX file';
                        }
                        if (file.size > 5 * 1024 * 1024) { // 5MB
                          return 'File size must be less than 5MB';
                        }
                        return true;
                      }
                    })
                  }}
                  render={({ field: rhfField, fieldState }) => (
                    <FormItem style={{ marginBottom: '1px' }}>
                      <FormLabel style={{
                        display: 'block',
                        marginBottom: '1px',
                        fontWeight: '500',
                        fontSize: '14px',
                        color: '#333'
                      }}>
                        {field.label}{field.required && ' *'}
                      </FormLabel>
                      <FormControl>
                        {field.type === 'textarea' ? (
                          <textarea 
                            {...rhfField} 
                            rows={3} 
                            style={{
                              width: '100%',
                              padding: '12px',
                              border: `1px solid ${fieldState.error ? '#dc2626' : '#ddd'}`,
                              borderRadius: '6px',
                              fontSize: '14px',
                              fontFamily: 'inherit',
                              backgroundColor: fieldState.error ? '#fef2f2' : 'white'
                            }}
                          />
                        ) : field.name === 'resume' ? (
                          <input 
                            type="file" 
                            accept=".pdf,.doc,.docx" 
                            onChange={e => rhfField.onChange(e.target.files)}
                            style={{
                              width: '100%',
                              padding: '12px',
                              border: `1px solid ${fieldState.error ? '#dc2626' : '#ddd'}`,
                              borderRadius: '6px',
                              fontSize: '14px',
                              backgroundColor: fieldState.error ? '#fef2f2' : 'white'
                            }}
                          />
                        ) : field.name === 'availability' ? (
                          <Select onValueChange={rhfField.onChange} value={rhfField.value}>
                            <SelectTrigger style={{
                              width: '100%',
                              padding: '12px',
                              border: `1px solid ${fieldState.error ? '#dc2626' : '#ddd'}`,
                              borderRadius: '6px',
                              fontSize: '14px',
                              backgroundColor: fieldState.error ? '#fef2f2' : 'white',
                              cursor: 'pointer'
                            }}>
                              <SelectValue placeholder="Select availability" />
                            </SelectTrigger>
                            <SelectContent style={{
                              zIndex: 10001,
                              backgroundColor: 'white',
                              border: '1px solid #ddd',
                              borderRadius: '6px',
                              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                            }}>
                              {availabilityOptions.map((option) => (
                                <SelectItem 
                                  key={option.value} 
                                  value={option.value}
                                  style={{
                                    padding: '12px 16px',
                                    cursor: 'pointer',
                                    fontSize: '14px'
                                  }}
                                >
                                  {option.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <input 
                            type={field.type} 
                            {...rhfField} 
                            style={{
                              width: '100%',
                              padding: '12px',
                              border: `1px solid ${fieldState.error ? '#dc2626' : '#ddd'}`,
                              borderRadius: '6px',
                              fontSize: '14px',
                              fontFamily: 'inherit',
                              backgroundColor: fieldState.error ? '#fef2f2' : 'white'
                            }}
                          />
                        )}
                      </FormControl>
                      {fieldState.error && (
                        <div style={{
                          color: '#dc2626',
                          fontSize: '12px',
                          marginTop: '4px',
                          fontWeight: '500'
                        }}>
                          {fieldState.error.message}
                        </div>
                      )}
                    </FormItem>
                  )}
                />
              ))}
              <div style={{
                gridColumn: '1 / -1',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '12px',
                marginTop: '0px',
                paddingTop: '10px',
                borderTop: '1px solid #eee'
              }}>
                <button 
                  type="button"
                  onClick={() => setResumeDialogOpen(false)} 
                  style={{
                    backgroundColor: '#6b7280',
                    color: 'white',
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  style={{
                    backgroundColor: '#1B365D',
                    color: 'white',
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Submit
                </button>
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Employer Requirements Dialog */}
      <Dialog open={employerDialogOpen} onOpenChange={setEmployerDialogOpen}>
        <DialogContent style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: 'white',
          border: '1px solid #ccc',
          borderRadius: '8px',
          padding: '24px',
          maxWidth: '600px',
          width: '90%',
          maxHeight: '90vh',
          overflowY: 'auto',
          zIndex: 10000,
          boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
        }}>
          <DialogHeader>
            <DialogTitle>Submit Hiring Requirements</DialogTitle>
            <DialogDescription>
              Tell us about your hiring needs and we'll connect you with the right talent.
            </DialogDescription>
          </DialogHeader>
          <Form {...employerForm}>
            <form onSubmit={employerForm.handleSubmit(handleEmployerFormSubmit)} style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '16px',
              marginTop: '20px'
            }}>
              {employerFields.map((field) => (
                <FormField
                  key={field.name}
                  name={field.name}
                  control={employerForm.control}
                  rules={{
                    required: field.required ? `${field.label} is required` : false,
                    ...(field.type === 'email' && {
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Invalid email address'
                      }
                    }),
                    ...(field.name === 'phone' && {
                      pattern: {
                        value: /^[\\+]?[1-9][\d]{0,15}$/,
                        message: 'Invalid phone number'
                      }
                    })
                  }}
                  render={({ field: rhfField, fieldState }) => (
                    <FormItem style={{ 
                      marginBottom: '16px',
                      gridColumn: field.name === 'requirements' ? '1 / -1' : 'auto'
                    }}>
                      <FormLabel style={{
                        display: 'block',
                        marginBottom: '8px',
                        fontWeight: '500',
                        color: '#333'
                      }}>
                        {field.label}{field.required && ' *'}
                      </FormLabel>
                      <FormControl>
                        {field.type === 'textarea' ? (
                          <textarea 
                            {...rhfField} 
                            placeholder={field.placeholder}
                            rows={4} 
                            style={{
                              width: '100%',
                              padding: '12px',
                              border: `1px solid ${fieldState.error ? '#dc2626' : '#ddd'}`,
                              borderRadius: '6px',
                              fontSize: '14px',
                              fontFamily: 'inherit',
                              backgroundColor: fieldState.error ? '#fef2f2' : 'white',
                              resize: 'vertical'
                            }}
                          />
                        ) : (
                          <input 
                            type={field.type} 
                            {...rhfField} 
                            placeholder={field.placeholder}
                            style={{
                              width: '100%',
                              padding: '12px',
                              border: `1px solid ${fieldState.error ? '#dc2626' : '#ddd'}`,
                              borderRadius: '6px',
                              fontSize: '14px',
                              fontFamily: 'inherit',
                              backgroundColor: fieldState.error ? '#fef2f2' : 'white'
                            }}
                          />
                        )}
                      </FormControl>
                      {fieldState.error && (
                        <div style={{
                          color: '#dc2626',
                          fontSize: '12px',
                          marginTop: '4px',
                          fontWeight: '500'
                        }}>
                          {fieldState.error.message}
                        </div>
                      )}
                    </FormItem>
                  )}
                />
              ))}
              <div style={{
                gridColumn: '1 / -1',
                display: 'flex',
                justifyContent: 'center',
                marginTop: '20px',
                paddingTop: '20px',
                borderTop: '1px solid #eee'
              }}>
                <button 
                  type="submit"
                  style={{
                    backgroundColor: '#1B365D',
                    color: 'white',
                    border: 'none',
                    padding: '12px 32px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    width: '100%',
                    justifyContent: 'center'
                  }}
                >
                  <span>Submit Requirements</span>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 19l7-7 3 3-7 7-3-3z"/>
                    <path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"/>
                    <path d="M2 2l7.586 7.586"/>
                    <circle cx="11" cy="11" r="2"/>
                  </svg>
                </button>
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UpdatedHomePage;